import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DropResult } from 'react-beautiful-dnd';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { removeTypename } from 'shared/helpers/utils';
import useErnie from 'shared/hooks/use-ernie';
import { GqlImageBanner } from 'types/graphql';
import { PreviewData } from '../components/preview';
import { ImageBanners, MenuBannerFormProps } from './image-banner.types';
import { reorder, sortByPosition, remove } from './image-banner-list/image-banner-list.utils';
import { useUpdateImageBanners } from '../data-access';

type UseImageBannerFormReturn = {
  imageBanners: ImageBanners;
  isDirty: boolean;
  isImageBannerLimitReached: boolean;
  handleAddImageBanner: (banner: GqlImageBanner) => void;
  handleRemoveBanner: (bannerIndex: number) => void;
  handleEditBanner: (bannerId: string) => void;
  onDragEnd: (result: DropResult) => void;
  previewData: PreviewData;
  handlePublish: () => Promise<void>;
  handleReset: () => void;
  showModal: boolean;
  addImageModal: () => void;
  closeModal: () => void;
  bannerToEdit: GqlImageBanner | null;
};

export function useMaxImageBanners(): number {
  const flags = useFlags();
  const showBannerSegmentation = flags['growth.ecomm.banner-segmentation.rollout'] ?? false;
  if (!showBannerSegmentation) {
    return 6;
  }
  const limit = flags['ecomm.admin.image-banner-limit.config'];
  return typeof limit === 'number' && limit > 0 ? limit : 50;
}

export function useImageBannerForm({ data }: MenuBannerFormProps): UseImageBannerFormReturn {
  const { imageBanners } = data;
  const showErnie = useErnie();

  const [showModal, setShowModal] = useState(false);
  const [localImageBanners, setLocalImageBanners] = useState<ImageBanners>(sortByPosition(imageBanners ?? []));
  const [bannerToEdit, setBannerToEdit] = useState<GqlImageBanner | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const maxImageBanners = useMaxImageBanners();
  const isImageBannerLimitReached = localImageBanners.length >= maxImageBanners;
  const { id: dispensaryId } = useParams<{ id: string }>();
  const updateData = useUpdateImageBanners();

  function addImageModal(): void {
    setBannerToEdit(null);
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
  }

  function handleAddImageBanner(banner: GqlImageBanner): void {
    const editImageIndex = localImageBanners.findIndex((b) => b._id === banner._id);

    // handling edit/add image banner
    if (editImageIndex > -1) {
      const updatedBanners = localImageBanners.map((existingBanner, index) =>
        index === editImageIndex ? { ...existingBanner, ...banner } : existingBanner
      );
      setLocalImageBanners(sortByPosition(updatedBanners));
    } else {
      const shiftedBanners = localImageBanners.map((existingBanner) => ({
        ...existingBanner,
        position: existingBanner.position + 1,
      }));
      setLocalImageBanners(sortByPosition([...shiftedBanners, banner]));
    }

    setIsDirty(true);
  }

  function handleRemoveBanner(bannerIndex: number): void {
    const newBanners = remove([...localImageBanners], bannerIndex);

    setLocalImageBanners(sortByPosition(newBanners));
    setIsDirty(true);
  }

  function handleEditBanner(bannerId: string): void {
    setShowModal(true);
    setBannerToEdit(localImageBanners.filter((banner) => banner._id === bannerId)[0]);
  }

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const newBanners = reorder([...localImageBanners], result.source.index, result.destination.index);

    setLocalImageBanners(newBanners);
    setIsDirty(true);
  };

  const handlePublish = async (): Promise<void> => {
    try {
      const imageBannersWithPlatforms = localImageBanners.map((banner: GqlImageBanner) => {
        const { ...rest } = banner;

        return {
          ...rest,
          platforms: rest.platforms ?? [],
        };
      });

      await updateData({
        variables: {
          dispensaryId,
          input: {
            imageBanners: removeTypename(imageBannersWithPlatforms),
          },
        },
      });

      showErnie('Your image banners have been updated', 'success');
      setIsDirty(false);
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handleReset = (): void => {
    setLocalImageBanners(sortByPosition(imageBanners ?? []));
  };

  const previewData = { ...data, imageBanners: localImageBanners };

  return {
    imageBanners: localImageBanners,
    isDirty,
    isImageBannerLimitReached,
    handleAddImageBanner,
    handleRemoveBanner,
    handleEditBanner,
    onDragEnd,
    previewData,
    handlePublish,
    handleReset,
    showModal,
    addImageModal,
    closeModal,
    bannerToEdit,
  };
}
